import React from 'react';
import {
  ResponsiveContainer,
  ResponsiveField,
  formAnimationList,
  formButtonList,
} from '@/src/strategies/util';
import moduleStyleRender from '../../ModuleStyle';
import Wrapper from 'src/components/Modules-revamp/BlockWrapper';
import BlockTitle from '@/src/components/Modules-revamp/BlockTitle';
import productTabRenderTemplate from '@/src/strategies/render/productTab';
import { get } from 'lodash';
import { EPreviewSize } from '@/src/types/common/SalesChannel';
import TabType from '@/src/components/Modules-revamp/TabType';
import Text from '@/src/components/Modules-revamp/Text';
import Animation from 'src/components/Modules-revamp/Animation';
import ModuleWidth from '@/src/components/Modules-revamp/ModuleWidthSelect';
import LeaveGapBetween from '@/src/components/Modules-revamp/LeaveGapBetween';
import { EnumModuleType } from '../../../../pages/SalesChannelContentEditor/common';
import OverflowItemsDisplayOption from '@/src/components/Modules-revamp/OverflowItemsDisplayOption';
import TabStyle from '@/src/components/Modules-revamp/TabStyle';
import AllTabs from '@/src/components/Modules-revamp/AllTabs';
import ProductTabEdit from '@/src/components/Modules-revamp/ProductTabEdit';
import { useProductTab } from '../../hooks/useProductTab';
import { ContentModuleGroupPreloadingContext } from '@/src/constrains/DraggableModules/PreloadComponent/SubscriptionPreloading';
import { Field } from '@/src/components/RcForm';
import { checkResponsiveProductTab } from 'src/constrains/SalesChannelContent-Revamp/ModuleList/validator';
import {
  tabs,
  products,
  productToShow,
} from 'src/tools/contentModulesFieldName';

type ResponsiveFieldProps = {
  field: ResponsiveField | ResponsiveField[];
  disabled?: boolean;
  sliderDisabled?: boolean;
  options?: Array<{ label: string; value: any }>;
  hasModal?: boolean;
  modalFunc?: any;
  extra?: any;
};

const productTabRenderer = (form: any, breakpoint: any, extra: any) => {
  const values = form.getFieldsValue(true);
  const { handleOnSectionReset, handleOnSectionCopy, salesChannelMode } = extra;
  const list = productTabRenderTemplate(values, breakpoint, salesChannelMode);
  const renderComponents = (
    {
      type,
      field,
    }: {
      title: string;
      type?: ResponsiveContainer;
      field: Array<any>;
    },
    breakpoint: any,
  ) => {
    if (type === ResponsiveContainer.ModuleStyle) {
      return field.map((f) => moduleStyleRender(f));
    } else {
      return field.map((f) => productTabComponent(f, breakpoint, extra));
    }
  };

  return (
    <>
      {list.map((item) => {
        const component = renderComponents(item, breakpoint);
        return (
          <Wrapper key={item.title} id={`${item?.id}`} breakpoint={breakpoint}>
            <BlockTitle
              id={item?.id}
              title={item?.title}
              breakpoint={breakpoint}
              onReset={() => handleOnSectionReset(item?.fields, breakpoint)}
              onCopy={() => handleOnSectionCopy(item?.fields, breakpoint)}
              headerErrorDependencies={get(item, 'headerErrorDependencies')}
              validator={get(item, 'validator')}
            />
            {component}
          </Wrapper>
        );
      })}
    </>
  );
};

const productTabComponent = (
  data: ResponsiveFieldProps,
  breakpoint: EPreviewSize,
  extra: any,
) => {
  const { animations, hoverAnimations, form, buttons } = extra || {};
  const draggableId = get(form.getFieldsValue(true), 'draggableId');
  const { onSort, onDelete, onReselect, onAdd, tabValidator } = extra;
  switch (data.field) {
    case ResponsiveField.TabType: {
      return (
        <TabType disabled={data?.disabled} options={data?.options || []} />
      );
    }
    case ResponsiveField.ModuleWidth: {
      return (
        <ModuleWidth disabled={data?.disabled} options={data?.options || []} />
      );
    }
    case ResponsiveField.LeaveGap: {
      return (
        <LeaveGapBetween
          disabled={data?.disabled}
          moduleType={EnumModuleType.ResponsiveProductTab}
        />
      );
    }
    case ResponsiveField.OverflowItems: {
      return (
        <OverflowItemsDisplayOption
          disabled={data?.disabled}
          options={data?.options || []}
        />
      );
    }
    case ResponsiveField.Animation: {
      return (
        <Animation
          disabled={data?.disabled}
          animationOptions={formAnimationList(animations || [])}
          hoverEffectOptions={formAnimationList(hoverAnimations || [])}
        />
      );
    }
    case ResponsiveField.ProductText: {
      return (
        <Text
          moduleType={EnumModuleType.ResponsiveProductCarousel}
          disabled={data?.disabled}
        />
      );
    }
    case ResponsiveField.TabStyle: {
      return (
        <TabStyle
          disabled={data?.disabled}
          sliderDisabled={data?.sliderDisabled}
        />
      );
    }
    case ResponsiveField.AllTabs: {
      return (
        <AllTabs
          disabled={data?.disabled}
          draggableId={draggableId}
          onSort={onSort}
          onAdd={onAdd}
          onDelete={onDelete}
          moduleType={EnumModuleType.ResponsivePosterTab}
          breakpoint={breakpoint}
        />
      );
    }
    case ResponsiveField.TabBarSetting: {
      return (
        <>
          <BlockTitle
            disabled={data?.disabled}
            title={data?.extra?.title}
            breakpoint={EPreviewSize.WIDE}
            validator={(d, extra) => {
              const tbs = get(d, tabs);
              const tab = get(tbs, data?.extra?.index);
              return tabValidator(tab, extra);
            }}
            headerErrorDependencies={[
              [tabs, data?.extra?.index, products],
              [tabs, data?.extra?.index, productToShow],
            ]}
          />
          <ProductTabEdit
            disabled={data?.disabled}
            options={data?.options || []}
            buttons={formButtonList(buttons)}
            breakpoint={breakpoint}
            index={data?.extra?.index}
            tabDraggableId={data?.extra?.tabDraggableId}
            onSort={onSort}
            onReselect={onReselect}
            onDelete={onDelete}
          />
        </>
      );
    }
  }
};

export default ({ form, breakpoint, ...extra }) => {
  const { handleOnAdd, handleOnDelete, handleOnReselect, handleOnSort } =
    useProductTab();
  const { buttons, animations, hoverAnimations } = React.useContext(
    ContentModuleGroupPreloadingContext,
  );
  const { checkEachTabCountError } = checkResponsiveProductTab();

  return (
    <Field shouldUpdate>
      {() =>
        productTabRenderer(form, breakpoint, {
          onAdd: handleOnAdd,
          onDelete: handleOnDelete,
          onSort: handleOnSort,
          onReselect: handleOnReselect,
          buttons,
          animations,
          hoverAnimations,
          form,
          ...extra,
          tabValidator: checkEachTabCountError,
        })
      }
    </Field>
  );
};
